// Chemin: C:\gestion-copro\src\services\document.service.ts
// Session 16 : Mise à jour types de documents
// Session 36 : Ajout fonction archiveEmail
// Session 44 : Suppression en cascade (affaires + chronologie)
// Session 46 : Rattachement automatique à l'affaire lors de l'archivage
// Session 63 : Compatibilité nouvelle taxonomie GED + anciens types
// Session 64 : Ajout getCategorieLabel, getCategorieCode, uploadDocument
// Session 111 : Ajout checkDuplicateByHash() — détection doublons SHA-256
// Session 112 : Incrémentation stockage (coproprietes + quotas) à l'upload et décrémentation à la suppression
// Session 126 : archiveEmail — taxonomie GED v2 (type=EML, categorie=SYN/PRO/GPA via categorieGED)
// Session 132 : Ajout donneesCles dans UploadDocumentParams et documentData Firestore
// Session 135 : CENTRALISATION — uploadDocument devient la porte unique d'entrée GED
//               Ajout champs SENTINELLE (source, anomalies, analyseSentinelle, typeDocumentLabel)
//               Support fichier déjà uploadé (file optionnel si métadonnées fournies)
// Session 142 : INTELLIGENCE CORRESPONDANCE NIVEAU 1
//               Ajout champs correspondance dans UploadDocumentParams et persistance Firestore :
//               courrierId_lie, courrierRef_lie, typeCorrespondance, qualiteReponse,
//               pointsTraites, pointsManquants, alerteQualite.
// Session 143 : CORRESPONDANCE MIROIR — Quand uploadDocument() archive un email/courrier,
//               création automatique d'une entrée dans la collection 'courriers' (statut 'recu').
//               Permet l'affichage dans "Mes correspondances" et le fonctionnement de l'Intelligence Correspondance.
// Session 144 : SUPPRESSION CASCADE — deleteDocument() supprime aussi le courrier miroir
//               associé (collection 'courriers' via documentGedId) pour éviter les courriers fantômes.
// Session 186 : FIX DOUBLONS — checkDuplicateByHash() maintenant appelé DANS uploadDocument()
//               avant toute écriture. Format erreur DOUBLON_DETECTE|message|docId pour traitement UI.
// Session 244 : Ajout checklistItemId + qualiteLecture dans UploadDocumentParams et documentData.
// Session 269 : Ajout champs bailleur (locataireId, moisConcerne, loyerHC, charges, total) pour filtrage fiche locataire.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  UpdateData,
  arrayUnion,
  increment
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase';
import {
  Document,
  DocumentCreateData,
  DocumentUpdateData,
  DocumentFilters,
  TypeDocument,
  TypeDocumentGED,
  TypeDocumentLegacy,
  TYPE_DOCUMENT_LABELS,
  CategorieDocument
} from '../types/document.types';
import {
  TYPES_DOCUMENT_GED,
  MAPPING_ANCIENS_TYPES,
  CATEGORIES_DOCUMENT_OPTIONS,
  CODES_CATEGORIES
} from '../constants/documentTaxonomy';
import { EmailDetail } from '../types/gmail.types';
import { Affaire } from '../types/affaire.types';

const COLLECTION_NAME = 'documents';

// ============================================
// DICTIONNAIRE UNIFIÉ DES LABELS (SESSION 63)
// Gère anciens ET nouveaux types
// ============================================

const ALL_TYPE_LABELS: Record<string, string> = {
  // Nouveaux types GED
  ...TYPES_DOCUMENT_GED,
  // Anciens types legacy
  ...TYPE_DOCUMENT_LABELS,
};

/**
 * Retourne le libellé français d'un type de document
 * Gère les anciens ET nouveaux types (Session 63)
 */
export function getTypeDocumentLabel(type: TypeDocument | string): string {
  return ALL_TYPE_LABELS[type] || type;
}

/**
 * Convertit un ancien type vers le nouveau système
 * Retourne le type tel quel s'il est déjà dans le nouveau format
 */
export function convertToNewType(type: string): TypeDocumentGED {
  // Si déjà un nouveau type, retourner tel quel
  if (type in TYPES_DOCUMENT_GED) {
    return type as TypeDocumentGED;
  }
  // Sinon, chercher dans le mapping
  const mapping = MAPPING_ANCIENS_TYPES[type];
  return mapping?.type || 'autre';
}

/**
 * Liste des types de documents pour les formulaires
 * Utilise les NOUVEAUX types GED (Session 63)
 */
export const TYPES_DOCUMENT: { value: TypeDocumentGED; label: string }[] = 
  Object.entries(TYPES_DOCUMENT_GED).map(([value, label]) => ({
    value: value as TypeDocumentGED,
    label,
  }));

/**
 * Liste incluant anciens ET nouveaux types (pour filtres/affichage)
 */
export const TOUS_TYPES_DOCUMENT: { value: string; label: string }[] = 
  Object.entries(ALL_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

// ============================================
// SESSION 64 : LABELS CATÉGORIES
// ============================================

/**
 * Retourne le libellé français d'une catégorie de document
 * Session 64 : Ajouté pour la refonte GED
 */
export function getCategorieLabel(categorie: CategorieDocument | string): string {
  const cat = CATEGORIES_DOCUMENT_OPTIONS.find(c => c.value === categorie);
  return cat ? cat.label : categorie;
}

/**
 * Retourne le code court d'une catégorie (ex: JUR, GPA, TEC)
 * Session 64 : Ajouté pour la refonte GED
 */
export function getCategorieCode(categorie: CategorieDocument | string): string {
  return CODES_CATEGORIES[categorie as CategorieDocument] || '';
}

// ============================================
// COULEURS DES BADGES PAR TYPE (SESSION 63)
// ============================================

export const TYPE_COLORS: Record<string, string> = {
  // Nouveaux types GED
  pv: 'bg-yellow-100 text-yellow-800',
  contrat: 'bg-purple-100 text-purple-800',
  facture: 'bg-red-100 text-red-800',
  devis: 'bg-orange-100 text-orange-800',
  courrier: 'bg-blue-100 text-blue-800',
  email: 'bg-indigo-100 text-indigo-800',
  rapport: 'bg-teal-100 text-teal-800',
  plan: 'bg-cyan-100 text-cyan-800',
  attestation: 'bg-lime-100 text-lime-800',
  reglement: 'bg-amber-100 text-amber-800',
  edd: 'bg-emerald-100 text-emerald-800',
  dpe: 'bg-green-100 text-green-800',
  permis: 'bg-sky-100 text-sky-800',
  photo: 'bg-pink-100 text-pink-800',
  note: 'bg-slate-100 text-slate-800',
  autre: 'bg-gray-100 text-gray-800',
  annexe: 'bg-red-50 text-red-700',
  appel_fonds: 'bg-orange-50 text-orange-700',
  budget: 'bg-violet-100 text-violet-800',
  releve: 'bg-rose-100 text-rose-800',
  // Anciens types legacy (pour période de transition)
  correspondance_entrante: 'bg-blue-100 text-blue-800',
  correspondance_sortante: 'bg-green-100 text-green-800',
  pv_compte_rendu: 'bg-yellow-100 text-yellow-800',
  rapport_diagnostic: 'bg-teal-100 text-teal-800',
  document_technique: 'bg-cyan-100 text-cyan-800',
  photo_video: 'bg-pink-100 text-pink-800',
  contrat_assurance: 'bg-purple-100 text-purple-800',
  document_administratif: 'bg-lime-100 text-lime-800',
};

/**
 * Retourne la couleur du badge pour un type de document
 */
export function getTypeColor(type: string): string {
  return TYPE_COLORS[type] || 'bg-gray-100 text-gray-600';
}

// ============================================
// SESSION 112 : GESTION STOCKAGE
// ============================================

/**
 * Convertit des bytes en Mo (arrondi à 2 décimales)
 */
function bytesToMo(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 100) / 100;
}

/**
 * Incrémente le stockage utilisé dans coproprietes + quotas (miroir)
 * Session 112 : Appelé après chaque upload réussi
 */
async function incrementerStockage(coproprieteId: string, fileSizeBytes: number): Promise<void> {
  if (!fileSizeBytes || fileSizeBytes <= 0) return;

  const tailleMo = bytesToMo(fileSizeBytes);

  try {
    // Mettre à jour coproprietes (source principale)
    const coproRef = doc(db, 'coproprietes', coproprieteId);
    await updateDoc(coproRef, {
      stockageUtiliseMo: increment(tailleMo),
      updatedAt: serverTimestamp()
    });

    // Mettre à jour quotas en miroir (si le document existe)
    const quotaRef = doc(db, 'quotas', coproprieteId);
    const quotaSnap = await getDoc(quotaRef);
    if (quotaSnap.exists()) {
      await updateDoc(quotaRef, {
        stockageUtiliseMo: increment(tailleMo),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    // Ne pas bloquer l'archivage si le compteur échoue
    console.error('[incrementerStockage] Erreur:', error);
  }
}

/**
 * Décrémente le stockage utilisé dans coproprietes + quotas (miroir)
 * Session 112 : Appelé après chaque suppression réussie
 */
async function decrementerStockage(coproprieteId: string, fileSizeBytes: number): Promise<void> {
  if (!fileSizeBytes || fileSizeBytes <= 0) return;

  const tailleMo = bytesToMo(fileSizeBytes);

  try {
    const coproRef = doc(db, 'coproprietes', coproprieteId);
    await updateDoc(coproRef, {
      stockageUtiliseMo: increment(-tailleMo),
      updatedAt: serverTimestamp()
    });

    const quotaRef = doc(db, 'quotas', coproprieteId);
    const quotaSnap = await getDoc(quotaRef);
    if (quotaSnap.exists()) {
      await updateDoc(quotaRef, {
        stockageUtiliseMo: increment(-tailleMo),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('[decrementerStockage] Erreur:', error);
  }
}

// ============================================
// SESSION 111 : DÉTECTION DOUBLONS PAR HASH
// ============================================

/**
 * Résultat de la vérification de doublon
 */
export interface DuplicateCheckResult {
  isDuplicate: boolean;
  existingDocumentId?: string;
  existingDocumentTitre?: string;
  existingDocumentDate?: Date;
}

/**
 * Vérifie si un document identique existe déjà pour cette copropriété.
 * 
 * Stratégie en 2 niveaux :
 *   1. Match exact par hash SHA-256 (pour les documents archivés depuis session 111+)
 *   2. Fallback par nom de fichier + taille (pour les documents anciens sans hash)
 * 
 * Le fallback nom+taille est fiable : deux fichiers différents ayant le même nom ET
 * la même taille au byte près est statistiquement improbable. Et c'est exactement
 * le scénario doublon réel : Nicole dépose deux fois le même PDF.
 */
export async function checkDuplicateByHash(
  coproprieteId: string,
  fileHash: string,
  fileName?: string,
  fileSize?: number
): Promise<DuplicateCheckResult> {
  try {
    // Niveau 1 : Match par hash SHA-256 (précis, pour documents récents)
    const hashQuery = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      where('fileHash', '==', fileHash),
      limit(1)
    );

    const hashSnapshot = await getDocs(hashQuery);

    if (!hashSnapshot.empty) {
      const existingDoc = hashSnapshot.docs[0];
      const data = existingDoc.data();
      return {
        isDuplicate: true,
        existingDocumentId: existingDoc.id,
        existingDocumentTitre: data.titre || data.fichierNom || 'Document sans titre',
        existingDocumentDate: data.createdAt?.toDate?.() || data.dateDocument?.toDate?.() || undefined
      };
    }

    // Niveau 2 : Fallback par nom de fichier + taille (pour documents sans hash)
    if (fileName && fileSize !== undefined) {
      const nameQuery = query(
        collection(db, COLLECTION_NAME),
        where('coproprieteId', '==', coproprieteId),
        where('fichierNom', '==', fileName),
        where('fichierTaille', '==', fileSize),
        limit(1)
      );

      const nameSnapshot = await getDocs(nameQuery);

      if (!nameSnapshot.empty) {
        const existingDoc = nameSnapshot.docs[0];
        const data = existingDoc.data();
        return {
          isDuplicate: true,
          existingDocumentId: existingDoc.id,
          existingDocumentTitre: data.titre || data.fichierNom || 'Document sans titre',
          existingDocumentDate: data.createdAt?.toDate?.() || data.dateDocument?.toDate?.() || undefined
        };
      }
    }

    return { isDuplicate: false };
  } catch (error) {
    console.error('[checkDuplicateByHash] Erreur vérification doublon:', error);
    // En cas d'erreur Firestore, on ne bloque pas l'archivage → pas de doublon détecté
    return { isDuplicate: false };
  }
}

// ============================================
// FONCTIONS CRUD DOCUMENTS
// ============================================

/**
 * Récupère tous les documents d'une copropriété
 */
export async function getDocuments(
  coproprieteId: string,
  filters?: DocumentFilters
): Promise<Document[]> {
  try {
    let q = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      orderBy('dateDocument', 'desc')
    );

    // Filtres additionnels
    if (filters?.type) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('coproprieteId', '==', coproprieteId),
        where('type', '==', filters.type),
        orderBy('dateDocument', 'desc')
      );
    }

    if (filters?.interlocuteurId) {
      q = query(
        collection(db, COLLECTION_NAME),
        where('coproprieteId', '==', coproprieteId),
        where('interlocuteurId', '==', filters.interlocuteurId),
        orderBy('dateDocument', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    
    let documents = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Document));

    // Filtres côté client (pour les dates et signalementId)
    if (filters?.dateDebut) {
      const dateDebut = Timestamp.fromDate(filters.dateDebut);
      documents = documents.filter(d => d.dateDocument >= dateDebut);
    }

    if (filters?.dateFin) {
      const dateFin = Timestamp.fromDate(filters.dateFin);
      documents = documents.filter(d => d.dateDocument <= dateFin);
    }

    if (filters?.signalementId) {
      documents = documents.filter(d => 
        d.signalementIds?.includes(filters.signalementId!)
      );
    }

    // Session 63 : Filtre par catégorie
    if (filters?.categorie) {
      documents = documents.filter(d => d.categorie === filters.categorie);
    }

    return documents;
  } catch (error) {
    console.error('Erreur lors de la récupération des documents:', error);
    throw error;
  }
}

/**
 * Récupère les documents liés à un signalement
 */
export async function getDocumentsBySignalement(
  coproprieteId: string,
  signalementId: string
): Promise<Document[]> {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      where('signalementIds', 'array-contains', signalementId),
      orderBy('dateDocument', 'desc')
    );

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Document));
  } catch (error) {
    console.error('Erreur lors de la récupération des documents par signalement:', error);
    throw error;
  }
}

/**
 * Récupère un document par son ID
 */
export async function getDocument(documentId: string): Promise<Document | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Document;
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du document:', error);
    throw error;
  }
}

/**
 * Crée un nouveau document (usage interne uniquement — préférer uploadDocument)
 * @deprecated Utiliser uploadDocument() pour tout archivage GED
 */
export async function createDocument(data: DocumentCreateData): Promise<string> {
  try {
    const documentData = {
      ...data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), documentData);
    return docRef.id;
  } catch (error) {
    console.error('Erreur lors de la création du document:', error);
    throw error;
  }
}

/**
 * Met à jour un document
 */
export async function updateDocument(
  documentId: string,
  data: DocumentUpdateData
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, documentId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    } as UpdateData<Document>);
  } catch (error) {
    console.error('Erreur lors de la mise à jour du document:', error);
    throw error;
  }
}

/**
 * Supprime un document et nettoie toutes les références
 * Session 44 : Suppression en cascade (fichier Storage + références affaires + chronologie)
 * Session 112 : Décrémente le stockage utilisé
 */
export async function deleteDocument(
  documentId: string,
  fichierUrl?: string
): Promise<void> {
  try {
    // 1. Récupérer les infos du document pour avoir coproprieteId et taille
    const document = await getDocument(documentId);
    if (!document) {
      throw new Error('Document non trouvé');
    }

    const coproprieteId = document.coproprieteId;
    const fichierTaille = document.fichierTaille || 0;

    // 2. Supprimer le fichier dans Storage (si existe)
    // Fix session 255 : Firebase Storage n'accepte pas les URLs HTTPS complètes dans ref().
    // Il faut extraire le chemin encodé depuis le paramètre "o" de l'URL, puis le décoder.
    const urlToDelete = fichierUrl || document.fichierUrl;
    if (urlToDelete) {
      try {
        let storagePath: string | null = null;
        try {
          const urlObj = new URL(urlToDelete);
          // Format Firebase Storage : /v0/b/<bucket>/o/<path encodé>
          const match = urlObj.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)/);
          if (match) {
            storagePath = decodeURIComponent(match[1]);
          }
        } catch {
          // URL non parseable — utiliser telle quelle (chemin direct)
          storagePath = urlToDelete;
        }
        if (storagePath) {
          const storageRef = ref(storage, storagePath);
          await deleteObject(storageRef);
        }
      } catch (storageError) {
        // Storage non bloquant : fichier peut être absent ou déjà supprimé
        console.warn('[deleteDocument] Fichier Storage non supprimé (non bloquant):', storageError);
      }
    }

    // 3. Nettoyer les références dans les affaires
    await nettoyerReferencesAffaires(documentId, coproprieteId);

    // 3bis. Session 144 : Supprimer le courrier miroir associé (si existe)
    await supprimerCourrierMiroir(documentId, coproprieteId);

    // 4. Supprimer le document dans Firestore
    const docRef = doc(db, COLLECTION_NAME, documentId);
    await deleteDoc(docRef);

    // 5. Session 112 : Décrémenter le stockage utilisé
    await decrementerStockage(coproprieteId, fichierTaille);
  } catch (error) {
    console.error('Erreur lors de la suppression du document:', error);
    throw error;
  }
}

/**
 * Nettoie les références d'un document dans toutes les affaires
 * - Retire le documentId du tableau documentIds
 * - Retire l'événement de chronologie associé
 */
async function nettoyerReferencesAffaires(
  documentId: string,
  coproprieteId: string
): Promise<void> {
  try {
    // Chercher toutes les affaires qui référencent ce document
    const q = query(
      collection(db, 'affaires'),
      where('coproprieteId', '==', coproprieteId),
      where('documentIds', 'array-contains', documentId)
    );

    const snapshot = await getDocs(q);

    // Pour chaque affaire trouvée, nettoyer les références
    for (const affaireDoc of snapshot.docs) {
      const affaire = affaireDoc.data() as Affaire;
      
      // Retirer le documentId du tableau
      const newDocumentIds = affaire.documentIds.filter(id => id !== documentId);
      
      // Retirer l'événement de chronologie associé
      const newEvenements = affaire.evenements.filter(
        evt => evt.documentId !== documentId
      );

      // Mettre à jour l'affaire
      const affaireRef = doc(db, 'affaires', affaireDoc.id);
      await updateDoc(affaireRef, {
        documentIds: newDocumentIds,
        evenements: newEvenements,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Erreur lors du nettoyage des références affaires:', error);
    // On ne bloque pas la suppression si le nettoyage échoue
  }
}

// ============================================
// SESSION 144 : SUPPRESSION COURRIER MIROIR
// Quand un document GED est supprimé, supprimer
// aussi le courrier miroir associé (si existe)
// ============================================

/**
 * Supprime le courrier miroir lié à un document GED.
 * Recherche dans la collection 'courriers' un document dont documentGedId == documentId.
 * Ne bloque pas la suppression du document GED si le courrier miroir n'existe pas ou si erreur.
 */
async function supprimerCourrierMiroir(documentGedId: string, coproprieteId: string): Promise<void> {
  try {
    const q = query(
      collection(db, 'courriers'),
      where('coproprieteId', '==', coproprieteId),
      where('documentGedId', '==', documentGedId),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      const courrierDoc = snapshot.docs[0];
      const courrierRef = doc(db, 'courriers', courrierDoc.id);
      await deleteDoc(courrierRef);
      console.log(`[supprimerCourrierMiroir] Courrier miroir ${courrierDoc.id} (${courrierDoc.data().numero || '?'}) supprimé pour document GED ${documentGedId}`);
    }
  } catch (error) {
    console.error('[supprimerCourrierMiroir] Erreur (non bloquante):', error);
    // On ne bloque pas la suppression du document GED
  }
}

/**
 * Formate la taille d'un fichier en format lisible
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 o';
  
  const k = 1024;
  const sizes = ['o', 'Ko', 'Mo', 'Go'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// ============================================
// SESSION 143 : COURRIER MIROIR (correspondance entrante)
// Quand SENTINELLE archive un email/courrier, on crée une entrée
// dans la collection 'courriers' pour que :
// 1. "Mes correspondances" affiche les entrants
// 2. getCourriersRecents() trouve les correspondances pour l'Intelligence Correspondance
// ============================================

interface CourrierMiroirParams {
  coproprieteId: string;
  documentGedId: string;      // ID du document GED associé
  objet: string;
  contenu: string;
  categorie: string;          // Catégorie GED (syndic, promoteur, etc.) → déduit le type destinataire
  interlocuteurNom: string;
  interlocuteurId?: string;
  modeEnvoi: 'email' | 'postal';
  dateDocument: Timestamp;
  userId: string;
  userNom: string;
  affaireId?: string;
  sens: 'entrant' | 'sortant'; // Session 143 : déterminé par SENTINELLE
}

/**
 * Déduit le type de destinataire depuis la catégorie GED
 */
function deduireTypeDestinataire(categorie: string): 'syndic' | 'promoteur' | 'entreprise' | 'coproprietaire' | 'autre' {
  const cat = categorie.toLowerCase();
  if (cat === 'syndic' || cat === 'syn') return 'syndic';
  if (cat === 'promoteur' || cat === 'pro') return 'promoteur';
  if (cat === 'gpa' || cat === 'travaux' || cat === 'technique' || cat === 'tec') return 'entreprise';
  if (cat === 'assurance' || cat === 'ass') return 'autre';
  if (cat === 'juridique' || cat === 'jur') return 'autre';
  return 'autre';
}

/**
 * Session 234 : Fallback nom expéditeur basé sur la catégorie GED
 * Evite "Expéditeur inconnu" quand SENTINELLE n'extrait pas le nom
 */
function deduireNomFallback(categorie: string): string {
  const cat = categorie.toLowerCase();
  if (cat === 'syndic' || cat === 'syn') return 'Syndic';
  if (cat === 'promoteur' || cat === 'pro') return 'Promoteur';
  if (cat === 'gpa') return 'GPA';
  if (cat === 'travaux' || cat === 'tec' || cat === 'technique') return 'Prestataire';
  if (cat === 'assurance' || cat === 'ass') return 'Assurance';
  if (cat === 'juridique' || cat === 'jur') return 'Juridique';
  if (cat === 'administratif' || cat === 'adm') return 'Administratif';
  return 'Expéditeur inconnu';
}

/**
 * Génère le prochain numéro de courrier (format COU-2026-001)
 * Duplicat local de la logique de courrier.service.ts pour éviter l'import circulaire
 */
async function generateNumeroCourrierLocal(coproprieteId: string): Promise<string> {
  const year = new Date().getFullYear();
  const prefix = `COU-${year}-`;

  const q = query(
    collection(db, 'courriers'),
    where('coproprieteId', '==', coproprieteId),
    where('numero', '>=', prefix),
    where('numero', '<', `COU-${year + 1}-`)
  );

  const snapshot = await getDocs(q);
  const nextNumber = (snapshot.size + 1).toString().padStart(3, '0');
  return `${prefix}${nextNumber}`;
}

/**
 * Crée une entrée miroir dans la collection 'courriers' pour une correspondance entrante
 * archivée via SENTINELLE. Statut = 'recu', sens = 'entrant'.
 *
 * Le document GED reste le fichier physique. Le courrier est la trace métier.
 */
async function creerCourrierMiroir(params: CourrierMiroirParams): Promise<string> {
  const {
    coproprieteId,
    documentGedId,
    objet,
    contenu,
    categorie,
    interlocuteurNom,
    interlocuteurId,
    modeEnvoi,
    dateDocument,
    userId,
    userNom,
    affaireId,
    sens,
  } = params;

  const now = Timestamp.now();
  const numero = await generateNumeroCourrierLocal(coproprieteId);

  // Session 143 : Le statut dépend du sens
  const statut = sens === 'sortant' ? 'envoye' : 'recu';

  const courrierData: Record<string, unknown> = {
    coproprieteId,
    numero,
    objet,
    contenu,
    type: 'autre' as const,        // Type générique — pas encore classifié en mise_en_demeure/relance/etc.
    modeEnvoi,
    destinataire: {
      type: deduireTypeDestinataire(categorie),
      nom: interlocuteurNom || deduireNomFallback(categorie),
      ...(interlocuteurId && { interlocuteurId }),
    },
    piecesJointes: [],
    // Session 143 : Statut et sens déterminés par SENTINELLE
    statut,
    sens,
    version: 1,
    // Lien vers le document GED
    documentGedId,
    sourceCreation: 'sentinelle_vigie',
    // Rédacteur = utilisateur qui a archivé
    redacteurId: userId,
    redacteurNom: userNom,
    dateCreation: dateDocument,      // Date du document original, pas de l'archivage
    dateModification: now,
    // Pas de validation pour un courrier reçu
    validations: {},
    votesRequis: 0,
    // Métadonnées
    createdAt: now,
    updatedAt: now,
  };

  // Session 143 : Pour les sortants, ajouter dateEnvoi
  if (sens === 'sortant') {
    courrierData.dateEnvoi = dateDocument;
    courrierData.envoyePar = userId;
  }

  // Rattachement optionnel à une affaire
  if (affaireId) {
    courrierData.affaireId = affaireId;
  }

  const docRef = await addDoc(collection(db, 'courriers'), courrierData);
  console.log(`[creerCourrierMiroir] Courrier miroir créé: ${docRef.id} (${numero}) pour document GED ${documentGedId}`);
  return docRef.id;
}

// ============================================
// SESSION 64 : UPLOAD DOCUMENT (depuis chat/analyse)
// SESSION 112 : Incrémentation stockage après upload
// SESSION 132 : Ajout donneesCles (données factuelles SENTINELLE)
// SESSION 135 : CENTRALISATION — Porte unique d'entrée GED
//   - file rendu optionnel (si fichier déjà uploadé, passer fichierNom/Taille/Type + tempFilePath)
//   - Ajout champs SENTINELLE : source, anomalies, analyseSentinelle, typeDocumentLabel
//   - Rattachement affaire + incrémentation stockage garantis pour TOUS les flux
// SESSION 142 : INTELLIGENCE CORRESPONDANCE — Ajout champs correspondance
// ============================================

/**
 * Session 135 : Données d'analyse SENTINELLE associées à un document
 */
export interface AnalyseSentinelleData {
  confiance: string;
  explication: string;
  actionsRecommandees: string[];
  anomalies: unknown[]; // Format variable selon la source (string[] ou objet structuré)
  donneesCles: Record<string, string>;
  dateAnalyse: unknown; // Timestamp Firestore
}

interface UploadDocumentParams {
  coproprieteId: string;
  titre: string;
  type: TypeDocumentGED | string; // Session 135 : accepte aussi les codes SENTINELLE (string)
  categorie: CategorieDocument | string; // Session 135 : idem
  description?: string;
  dateDocument?: Date;
  interlocuteurId?: string;
  interlocuteurNom?: string;
  affaireId?: string;
  visibilite?: 'cs_only' | 'public' | 'proprietaire';
  userId: string;
  userNom: string;
  fileHash?: string; // Session 111 : Hash SHA-256 pour détection doublons
  donneesCles?: Record<string, string>; // Session 132 : Données factuelles extraites par SENTINELLE

  // --- Option A : fichier brut (upload par le service) ---
  file?: File;

  // --- Option B : fichier déjà uploadé (SENTINELLE VIGIE) ---
  tempFilePath?: string; // URL Storage du fichier déjà uploadé
  fichierNom?: string;   // Nom du fichier (requis si pas de file)
  fichierTaille?: number; // Taille en bytes (requis si pas de file)
  fichierType?: string;   // MIME type (requis si pas de file)

  // --- Session 135 : Champs SENTINELLE (optionnels) ---
  source?: string; // ex: 'sentinelle_vigie', 'chat', 'archivage_email'
  typeDocumentLabel?: string; // Libellé humain du type (ex: "Procès-verbal")
  anomalies?: unknown[]; // Format variable (string[] depuis SENTINELLE, objet structuré possible)
  analyseSentinelle?: AnalyseSentinelleData;

  // --- Session 142 : Intelligence Correspondance ---
  courrierId_lie?: string;
  courrierRef_lie?: string;
  typeCorrespondance?: string; // 'reponse' | 'relance' | 'accuseReception'
  qualiteReponse?: string;     // 'complete' | 'partielle' | 'incomplete'
  pointsTraites?: string[];
  pointsManquants?: string[];
  alerteQualite?: string;
  // --- Session 143 : Sens correspondance ---
  sensCorrespondance?: 'entrant' | 'sortant' | null;
  // --- Session 186 : Skip doublon (pour "Archiver quand même") ---
  skipDuplicateCheck?: boolean;
  // --- Session 244 : Checklist et qualité de lecture ---
  checklistItemId?: string | null;
  qualiteLecture?: 'bonne' | 'moyenne' | 'faible' | null;
  // --- Session 269 : Champs bailleur (appel de loyer + quittance) ---
  locataireId?: string;
  locataireEmail?: string;
  moisConcerne?: string;
  loyerHC?: number;
  charges?: number;
  total?: number;
}

/**
 * Upload un document dans la GED — PORTE UNIQUE D'ENTRÉE
 * 
 * Session 64 : Création initiale
 * Session 111 : Stocke le fileHash si fourni
 * Session 112 : Incrémente le stockage utilisé après upload réussi
 * Session 132 : Stocke les donneesCles si fournies
 * Session 135 : CENTRALISATION — Accepte 2 modes :
 *   Mode A : file fourni → upload Storage + écriture Firestore
 *   Mode B : tempFilePath + métadonnées → écriture Firestore seule (fichier déjà uploadé)
 *   Dans les 2 cas : rattachement affaire + incrémentation stockage garantis.
 * Session 142 : Stocke les champs correspondance si fournis
 */
export async function uploadDocument(params: UploadDocumentParams): Promise<string> {
  const {
    coproprieteId,
    file,
    titre,
    type,
    categorie,
    description,
    dateDocument,
    interlocuteurId,
    interlocuteurNom,
    affaireId,
    visibilite = 'cs_only',
    userId,
    userNom,
    tempFilePath,
    fileHash,
    donneesCles,
    // Session 135 : Champs SENTINELLE
    source,
    typeDocumentLabel,
    anomalies,
    analyseSentinelle,
    // Session 142 : Champs correspondance
    courrierId_lie,
    courrierRef_lie,
    typeCorrespondance,
    qualiteReponse,
    pointsTraites,
    pointsManquants,
    alerteQualite,
    // Session 143 : Sens correspondance
    sensCorrespondance,
    // Session 186 : Skip doublon pour "Archiver quand même"
    skipDuplicateCheck,
    // Session 244 : Checklist et qualité de lecture
    checklistItemId,
    qualiteLecture,
    locataireId,
    locataireEmail,
    moisConcerne,
    loyerHC,
    charges,
    total,
  } = params;

  // Session 135 : Résolution des métadonnées fichier (File ou paramètres directs)
  let fichierUrl: string;
  let fichierNom: string;
  let fichierTaille: number;
  let fichierType: string;

  if (file) {
    // Mode A : On a un objet File
    fichierNom = file.name;
    fichierTaille = file.size;
    fichierType = file.type;
  } else if (params.fichierNom && params.fichierTaille !== undefined && params.fichierType) {
    // Mode B : Métadonnées fournies directement (fichier déjà uploadé)
    fichierNom = params.fichierNom;
    fichierTaille = params.fichierTaille;
    fichierType = params.fichierType;
  } else {
    throw new Error('[uploadDocument] Paramètres insuffisants : fournir file OU (fichierNom + fichierTaille + fichierType + tempFilePath)');
  }

  try {
    // Session 186 : VÉRIFICATION DOUBLON — avant toute action
    // Sauf si skipDuplicateCheck = true (cas "Archiver quand même")
    if (!skipDuplicateCheck) {
      const duplicateCheck = await checkDuplicateByHash(
        coproprieteId,
        fileHash || '',
        fichierNom,
        fichierTaille
      );

      if (duplicateCheck.isDuplicate) {
        const dateStr = duplicateCheck.existingDocumentDate
          ? ` (archivé le ${duplicateCheck.existingDocumentDate.toLocaleDateString('fr-FR')})`
          : '';
        throw new Error(
          `DOUBLON_DETECTE|Ce document existe déjà dans ta GED : "${duplicateCheck.existingDocumentTitre}"${dateStr}|${duplicateCheck.existingDocumentId}`
        );
      }
    }

    // Résolution de l'URL du fichier
    if (tempFilePath) {
      // Fichier déjà uploadé dans Storage (SENTINELLE VIGIE ou autre)
      fichierUrl = tempFilePath;
    } else if (file) {
      // Upload du fichier vers Storage
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `coproprietes/${coproprieteId}/documents/${timestamp}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      
      await uploadBytes(storageRef, file);
      fichierUrl = await getDownloadURL(storageRef);
    } else {
      throw new Error('[uploadDocument] Aucune source fichier : fournir file ou tempFilePath');
    }

    // Construire le document Firestore
    // Session 111 : Ajout fileHash pour détection doublons
    // Session 135 : Ajout champs SENTINELLE optionnels
    // Session 142 : Ajout champs correspondance optionnels
    const documentData: Record<string, unknown> = {
      coproprieteId,
      titre,
      type,
      categorie,
      description: description || null,
      fichierUrl,
      fichierNom,
      fichierTaille,
      fichierType,
      interlocuteurId: interlocuteurId || null,
      interlocuteurNom: interlocuteurNom || null,
      dateDocument: dateDocument ? Timestamp.fromDate(dateDocument) : Timestamp.now(),
      visibilite,
      affaireId: affaireId || null,
      uploadeParId: userId,
      uploadeParNom: userNom,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Champs conditionnels (on n'écrit que s'ils ont une valeur)
    if (fileHash) documentData.fileHash = fileHash;
    if (donneesCles && Object.keys(donneesCles).length > 0) documentData.donneesCles = donneesCles;
    if (source) documentData.source = source;
    if (typeDocumentLabel) documentData.typeDocumentLabel = typeDocumentLabel;
    if (anomalies && anomalies.length > 0) documentData.anomalies = anomalies;
    if (analyseSentinelle) documentData.analyseSentinelle = analyseSentinelle;

    // Session 142 : Champs correspondance (on n'écrit que si correspondance détectée)
    if (courrierId_lie) documentData.courrierId_lie = courrierId_lie;
    if (courrierRef_lie) documentData.courrierRef_lie = courrierRef_lie;
    if (typeCorrespondance) documentData.typeCorrespondance = typeCorrespondance;
    if (qualiteReponse) documentData.qualiteReponse = qualiteReponse;
    if (pointsTraites && pointsTraites.length > 0) documentData.pointsTraites = pointsTraites;
    if (pointsManquants && pointsManquants.length > 0) documentData.pointsManquants = pointsManquants;
    if (alerteQualite) documentData.alerteQualite = alerteQualite;

    // Session 297/298 : Sens correspondance — posé sur le document GED pour badge Reçu/Envoyé
    // Écriture inconditionnelle : null = document sans sens (non-correspondance), valide en Firestore
    documentData.sensCorrespondance = sensCorrespondance ?? null;
    // Session 244 : Checklist item identifié par SENTINELLE + qualité de lecture
    if (checklistItemId) documentData.checklistItemId = checklistItemId;
    if (qualiteLecture) documentData.qualiteLecture = qualiteLecture;
    // Session 269 : Champs bailleur
    if (locataireId) documentData.locataireId = locataireId;
    if (locataireEmail) documentData.locataireEmail = locataireEmail;
    if (moisConcerne) documentData.moisConcerne = moisConcerne;
    if (loyerHC !== undefined) documentData.loyerHC = loyerHC;
    if (charges !== undefined) documentData.charges = charges;
    if (total !== undefined) documentData.total = total;

    const docRef = await addDoc(collection(db, COLLECTION_NAME), documentData);
    const documentId = docRef.id;

    // Session 298 : Création courrier miroir pour toute correspondance (email/courrier)
    // Porte unique supprimée — le miroir est créé quel que soit le flux d'entrée
    const typesCorrespondance = ['email', 'courrier', 'EML'];
    if (typesCorrespondance.includes(type)) {
      try {
        await creerCourrierMiroir({
          coproprieteId,
          documentGedId: documentId,
          objet: titre,
          contenu: description || '',
          categorie: categorie as string,
          interlocuteurNom: interlocuteurNom || '',
          interlocuteurId: interlocuteurId || undefined,
          modeEnvoi: type === 'email' || type === 'EML' ? 'email' : 'postal',
          dateDocument: dateDocument ? Timestamp.fromDate(dateDocument) : Timestamp.now(),
          userId,
          userNom,
          affaireId: affaireId || undefined,
          sens: sensCorrespondance || 'entrant', // Fallback entrant si SENTINELLE n'a pas déterminé
        });
      } catch (err) {
        // Ne pas bloquer l'archivage si la création miroir échoue
        console.error('[uploadDocument] Erreur création courrier miroir:', err);
      }
    }

    // Si une affaire est spécifiée, rattacher automatiquement le document
    if (affaireId) {
      try {
        await rattacherDocumentAffaire(affaireId, documentId, titre, userId, userNom);
      } catch (err) {
        console.error('Erreur rattachement automatique à l\'affaire:', err);
        // On ne bloque pas - le document est créé
      }
    }

    // Session 112 : Incrémenter le stockage utilisé
    await incrementerStockage(coproprieteId, fichierTaille);

    return documentId;
  } catch (error) {
    console.error('Erreur lors de l\'upload du document:', error);
    throw error;
  }
}

// ===========================================
// SESSION 36 : ARCHIVAGE EMAIL → GED
// SESSION 46 : RATTACHEMENT AUTOMATIQUE AFFAIRE
// SESSION 63 : Ajout catégorie par défaut
// SESSION 112 : Incrémentation stockage pour pièces jointes
// ===========================================

interface ArchiveEmailParams {
  coproprieteId: string;
  email: EmailDetail;
  titre: string;
  categorie: string;
  categorieGED?: string; // Session 126 : Catégorie GED v2 (SYN, PRO, GPA...)
  affaireId?: string;
  selectedAttachmentIds: string[];
  userId: string;
  userNom: string;
}

/**
 * Archive un email dans la GED avec ses pièces jointes
 * Session 46 : Rattache automatiquement le document à l'affaire si spécifiée
 * Session 63 : Ajoute catégorie et visibilité par défaut
 * Session 112 : Comptabilise le stockage des pièces jointes
 */
export async function archiveEmail(params: ArchiveEmailParams): Promise<string> {
  const { coproprieteId, email, titre, categorie, categorieGED, affaireId, selectedAttachmentIds, userId, userNom } = params;

  // Guard anti-doublon par messageId — empêche l'archivage multiple du même email
  // (double-clic sur "Archiver" ou rechargement pendant l'opération)
  try {
    const existingQuery = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId),
      where('sourceEmail.messageId', '==', email.id)
    );
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) {
      // Email déjà archivé — retourner l'ID existant au lieu de créer un doublon
      console.warn('[archiveEmail] Email déjà archivé, doublon bloqué:', email.id);
      return existingSnap.docs[0].id;
    }
  } catch (dupErr) {
    console.error('[archiveEmail] Erreur vérification doublon (non bloquant):', dupErr);
    // On continue — mieux vaut créer un doublon que bloquer l'archivage
  }

  const pieceJointes: string[] = [];
  let totalPJSize = 0; // Session 112 : cumul taille pièces jointes
  
  // 1. Télécharger et uploader les pièces jointes sélectionnées
  if (selectedAttachmentIds.length > 0 && email.attachments) {
    const fetchAttachment = httpsCallable(functions, 'fetchAttachment');
    
    for (const attachmentId of selectedAttachmentIds) {
      const attachment = email.attachments.find(a => a.id === attachmentId);
      if (!attachment) continue;
      
      try {
        // Récupérer le contenu via Cloud Function
        const result = await fetchAttachment({
          coproprieteId,
          messageId: email.id,
          attachmentId: attachment.id
        });
        
        const { data: base64Data } = result.data as { data: string };
        
        // Convertir base64 en Blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: attachment.mimeType });
        
        // Session 112 : Comptabiliser la taille
        totalPJSize += byteArray.length;
        
        // Upload dans Storage
        const timestamp = Date.now();
        const safeName = attachment.filename.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = `coproprietes/${coproprieteId}/documents/emails/${email.id}/${timestamp}_${safeName}`;
        const storageRef = ref(storage, storagePath);
        
        await uploadBytes(storageRef, blob);
        const downloadUrl = await getDownloadURL(storageRef);
        pieceJointes.push(downloadUrl);
        
      } catch (err) {
        console.error(`Erreur upload pièce jointe ${attachment.filename}:`, err);
        // Continue avec les autres
      }
    }
  }
  
  // 2. Créer le document dans Firestore
  // Session 63 : Utiliser le nouveau type et ajouter catégorie/visibilité
  // Session 126 : Taxonomie GED v2 — type = code GED (EML par défaut), catégorie = code GED (SYN par défaut)
  const documentData = {
    coproprieteId,
    titre,
    type: categorie || 'EML', // Le select "catégorie" du modal contient en réalité le type de document
    categorie: categorieGED || 'SYN', // Session 126 : Catégorie GED v2 depuis SENTINELLE
    visibilite: 'cs_only',
    description: `Email archivé de ${email.from}`,
    fichierUrl: '',
    fichierNom: `email_${email.id}.eml`,
    fichierTaille: totalPJSize, // Session 112 : Taille réelle des PJ au lieu de 0
    fichierType: 'message/rfc822',
    sourceEmail: {
      messageId: email.id,
      from: email.from,
      to: email.to,
      date: Timestamp.fromDate(new Date(email.date)),
      subject: email.subject
    },
    contenuTexte: email.bodyText || '',
    pieceJointes,
    dateDocument: Timestamp.fromDate(new Date(email.date)),
    uploadePar: userId,
    uploadeParNom: userNom,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    sens: 'recu',
    ...(affaireId && { affaireId })
  };
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), documentData);
  const documentId = docRef.id;
  
  // 3. SESSION 46 : Si une affaire est spécifiée, rattacher automatiquement le document
  if (affaireId) {
    try {
      await rattacherDocumentAffaire(affaireId, documentId, titre, userId, userNom);
    } catch (err) {
      console.error('Erreur rattachement automatique à l\'affaire:', err);
      // On ne bloque pas - le document est créé, le rattachement peut être fait manuellement
    }
  }

  // 4. Session 112 : Incrémenter le stockage (taille pièces jointes)
  if (totalPJSize > 0) {
    await incrementerStockage(coproprieteId, totalPJSize);
  }
  
  return documentId;
}

/**
 * Rattache un document à une affaire
 * - Ajoute le documentId au tableau documentIds de l'affaire
 * - Ajoute un événement dans la chronologie
 */
async function rattacherDocumentAffaire(
  affaireId: string,
  documentId: string,
  documentTitre: string,
  userId: string,
  userNom: string
): Promise<void> {
  const affaireRef = doc(db, 'affaires', affaireId);
  
  // Créer l'événement de chronologie
  const evenement = {
    id: crypto.randomUUID(),
    type: 'document',
    date: Timestamp.now(),
    titre: `Document ajouté: ${documentTitre}`,
    description: `Dossier "${documentTitre}" créé`,
    auteurId: userId,
    auteurNom: userNom,
    documentId: documentId
  };
  
  await updateDoc(affaireRef, {
    documentIds: arrayUnion(documentId),
    evenements: arrayUnion(evenement),
    updatedAt: Timestamp.now()
  });
}

/**
 * Récupère les IDs Gmail des emails déjà archivés pour une copropriété
 * Permet de les filtrer dans la messagerie
 */
export async function getArchivedEmailIds(coproprieteId: string): Promise<string[]> {
  try {
    // Récupérer tous les documents de la copropriété
    const q = query(
      collection(db, COLLECTION_NAME),
      where('coproprieteId', '==', coproprieteId)
    );
    
    const snapshot = await getDocs(q);
    
    const emailIds: string[] = [];
    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      // Vérifier si c'est un email archivé (a un sourceEmail avec messageId)
      if (data.sourceEmail && data.sourceEmail.messageId) {
        emailIds.push(data.sourceEmail.messageId);
      }
    });
    
    console.log('Emails archivés trouvés:', emailIds);
    return emailIds;
  } catch (error) {
    console.error('Erreur récupération emails archivés:', error);
    return [];
  }
}

// ============================================================================
// SESSION 232 : ARCHIVAGE DOCUMENT — EXCLUSION DU CONTEXTE SENTINELLE
// Un document archivé reste visible dans la GED mais n'est plus injecté
// dans le contexte SENTINELLE (ne fausse plus l'analyse de la situation actuelle)
// ============================================================================

/**
 * Archive un document — il sera exclu du contexte SENTINELLE
 * Le document reste visible dans la GED avec badge "Archivé"
 */
export async function archiverDocument(documentId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, documentId);
  await updateDoc(docRef, {
    estArchive: true,
    updatedAt: serverTimestamp()
  });
  // Session 233 : Propager estArchive vers le courrier miroir associé (si existe)
  await propagerArchivageVersCourrier(documentId, true);
}

/**
 * Désarchive un document — il redevient visible pour SENTINELLE
 */
export async function desarchiverDocument(documentId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, documentId);
  await updateDoc(docRef, {
    estArchive: false,
    updatedAt: serverTimestamp()
  });
  // Session 233 : Propager estArchive vers le courrier miroir associé (si existe)
  await propagerArchivageVersCourrier(documentId, false);
}

/**
 * Session 233 : Propage estArchive vers le courrier miroir lié à un document GED.
 * Session 234 : Double recherche pour couvrir les courriers sans lien documentGedId :
 *   Passe 1 — cherche dans 'courriers' un doc dont documentGedId == documentId (courriers miroirs créés par creerCourrierMiroir)
 *   Passe 2 — si rien trouvé, lit le document GED pour récupérer courrierId_lie et met à jour ce courrier directement
 * Ne bloque pas si aucun courrier associé n'existe.
 */
async function propagerArchivageVersCourrier(documentGedId: string, estArchive: boolean): Promise<void> {
  try {
    // Passe 1 : courrier miroir créé par creerCourrierMiroir (a un champ documentGedId)
    const snapshot = await getDocs(
      query(
        collection(db, 'courriers'),
        where('documentGedId', '==', documentGedId)
      )
    );
    if (!snapshot.empty) {
      await updateDoc(snapshot.docs[0].ref, { estArchive });
      return;
    }

    // Passe 2 : courrier créé manuellement ou via un autre flux
    // Le document GED peut avoir un champ courrierId_lie pointant vers le courrier
    const gedSnap = await getDoc(doc(db, COLLECTION_NAME, documentGedId));
    if (!gedSnap.exists()) return;
    const courrierId = gedSnap.data()?.courrierId_lie as string | undefined;
    if (!courrierId) return;

    const courrierRef = doc(db, 'courriers', courrierId);
    const courrierSnap = await getDoc(courrierRef);
    if (!courrierSnap.exists()) return;
    await updateDoc(courrierRef, { estArchive });
  } catch (err) {
    console.error('[propagerArchivageVersCourrier] Erreur (non bloquant):', err);
  }
}
